import { useTranslation } from 'react-i18next';
import { ComponentType, useEffect, useState } from 'react';
import { Editor as SlateEditor, Element as SlateElement } from 'slate';
import { toggleBlock } from 'slate/functions';
import { CustomEditor } from 'slate/types/slate';
import { useSlate } from 'slate-react';
import { toolbarCSSExtractor } from 'shared/lib/css';
import { Dropdown } from 'app/components';
import { DropdownItem } from 'shared/forms/Dropdown';
import { useUser } from 'app/state/contexts/UserContext';
import { useEditorCss } from 'app/api/documents/useEditorData';
import { useEditorCaret, useEditorCoarse } from 'app/state/contexts/EditorUiContext';
import { parseCellFormat } from 'shared/lib/cell-format';

const STRUCTURAL_FORMAT_TO_TYPE: Record<string, string> = {
  li: 'list-item',
  ul: 'bulleted-list',
  ol: 'numbered-list',
};

type OptionValue = {
  format?: string;
  class?: string;
};

interface FormatOption {
  id?: number;
  value: OptionValue;
  title?: string;
  requiredClasses?: string[];
}

interface FormattingNode {
  formatting?: { format?: string; class?: string };
}

interface ActiveSection {
  templateSuperId?: string;
  type?: string;
}

interface ToolbarDefinition {
  templateId?: string;
  options: { title: string; selector: string }[];
  wrapperClasses?: string[];
  [key: string]: unknown;
}

export const getActiveBlockId = (
  node: FormattingNode | null | undefined,
  formats: FormatOption[] | null | undefined,
  editor?: CustomEditor
): FormatOption | null => {
  const activeFormat = node?.formatting?.format;
  const activeClass = node?.formatting?.class;

  if ((activeFormat || activeClass) && Array.isArray(formats)) {
    // First try to match compound selectors (both format + class)
    const compoundMatch = formats.find((f) => f.value.format && f.value.class && f.value.format === activeFormat && f.value.class === activeClass);
    if (compoundMatch) return compoundMatch;

    // A class is more specific than a bare tag, so check class-only options
    // before format-only ones — otherwise a node like
    // { format: 'p', class: 'ingress' } matches the plain `p` option.
    const classMatch = activeClass ? formats.find((f) => !f.value.format && f.value.class === activeClass) : undefined;
    if (classMatch) {
      return classMatch;
    }

    const formatMatch = activeFormat ? formats.find((f) => !f.value.class && f.value.format === activeFormat) : undefined;
    if (formatMatch) {
      return formatMatch;
    }
  }

  if (editor?.selection && Array.isArray(formats)) {
    for (const f of formats) {
      const fmt = f.value?.format;
      const targetType = fmt ? STRUCTURAL_FORMAT_TO_TYPE[fmt] : undefined;
      if (!targetType) continue;
      const [ancestor] = SlateEditor.nodes(editor, {
        match: (n) => SlateElement.isElement(n) && n.type === targetType,
      });
      if (!ancestor) continue;
      const [ancestorNode] = ancestor;
      if (f.value.class) {
        if ((ancestorNode as FormattingNode).formatting?.class === f.value.class) return f;
      } else {
        if (!(ancestorNode as FormattingNode).formatting?.class) return f;
      }
    }
  }

  return null;
};

/**
 * When the user clicks a new page that has a different template see if that template has new
 * typography options under the .toolbar class (this is done in toolbarCSSExtractor())
 *
 * @param {*} reduxActiveSection
 * @param {*} toolbarDefinition
 * @returns
 */
export const getOptionsForCurrentTemplate = (
  reduxActiveSection: ActiveSection | null = {},
  toolbarDefinition: ToolbarDefinition[] | null = []
): FormatOption[] => {
  if (!reduxActiveSection) {
    return [];
  }

  //Fetch global toolbar layout
  const active = toolbarDefinition?.filter((toolbar) => toolbar.templateId === undefined || toolbar.templateId === reduxActiveSection.templateSuperId) ?? [];

  // Map toolbar definitions to dropdown options.
  const options = active.flatMap((toolbar, index) => {
    return toolbar.options.map((option) => {
      const entry: FormatOption = {
        id: index,
        value: {},
        title: option.title,
        requiredClasses: toolbar.wrapperClasses,
      };

      // Parse selector: "h5.ingress" → { format: 'h5', class: 'ingress' }
      // ".ingress" → { class: 'ingress' }
      // "h1" → { format: 'h1' }
      const { element, className } = parseCellFormat(option.selector);
      if (element) entry.value['format'] = element;
      if (className) entry.value['class'] = className;
      return entry;
    });
  });

  return options;
};

const DropItemComponent = ({ item }: { item: FormatOption }) => {
  const cssClass = item.value?.class || '';
  let child = (
    <p className={`m-0 ${cssClass}`}>
      <span>{item.title}</span>
    </p>
  );

  switch (item.value?.format) {
    case 'h1':
      child = <h1 className={`fs-3 my-0 ${cssClass}`}>{item.title}</h1>;
      break;
    case 'h2':
      child = <h2 className={`fs-4 my-0 ${cssClass}`}>{item.title}</h2>;
      break;
    case 'h3':
      child = <h3 className={`fs-5 my-0 ${cssClass}`}>{item.title}</h3>;
      break;
    case 'h4':
      child = <h4 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h4>;
      break;
    case 'h5':
      child = <h5 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h5>;
      break;
    case 'h6':
      child = <h6 className={`fs-6 my-0 ${cssClass}`}>{item.title}</h6>;
      break;
    default:
      break;
  }

  const innerClass = !!item.requiredClasses ? item.requiredClasses[item.requiredClasses.length - 1] : '';
  return (
    <div className="page-wrapper force-text">
      <div className={innerClass}>{child}</div>
    </div>
  );
};

export const MarkHeadingDropdown = () => {
  const { t } = useTranslation('dropdown');

  const { user } = useUser();
  const locale = user?.userProperties?.cultureId ?? 'sv';

  const defaultOptions = [
    {
      value: { format: 'paragraph' },
      title: t('format.paragraph'),
    },
    {
      value: { format: 'h1' },
      title: t('format.heading-1'),
    },
    {
      value: { format: 'h2' },
      title: t('format.heading-2'),
    },
    {
      value: { format: 'h3' },
      title: t('format.heading-3'),
    },
  ];
  const [value, setValue] = useState<OptionValue | undefined>(defaultOptions[0].value);
  const [options, setOptions] = useState<FormatOption[]>(defaultOptions);

  const placeholderValue = (opts: { value: { class?: string; format?: string } }[]) =>
    (opts.find((option) => !option.value.class && (option.value.format === 'p' || option.value.format === 'paragraph')) ?? opts[0])?.value;
  const editor = useSlate();

  const reduxActiveSection = useEditorCoarse().activeSection;
  const reduxActiveNode = useEditorCaret().activeNode;
  const reduxDocumentCSS = useEditorCss().shadowedCss;

  useEffect(() => {
    const block = getActiveBlockId(reduxActiveNode, options, editor);
    setValue(block?.value || placeholderValue(options));
  }, [reduxActiveNode, options, editor]);

  useEffect(() => {
    if (reduxActiveSection?.type) {
      const templateOptions = getOptionsForCurrentTemplate(reduxActiveSection, toolbarCSSExtractor(reduxDocumentCSS, locale, 'toolbar'));
      const nextOptions = templateOptions.length > 0 ? templateOptions : defaultOptions;

      setOptions(nextOptions);
      setValue(placeholderValue(nextOptions));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxActiveSection, reduxDocumentCSS]);

  const handleChange = (newFormat: unknown) => {
    const format = newFormat as OptionValue;
    setValue(format);
    toggleBlock(editor, 'custom-formatting', format);
  };

  const handleSelect = (newFormat: unknown) => {
    const format = newFormat as OptionValue;
    if (format?.format && STRUCTURAL_FORMAT_TO_TYPE[format.format] && JSON.stringify(format) === JSON.stringify(value)) {
      toggleBlock(editor, 'custom-formatting', format);
    }
  };

  return (
    <Dropdown
      sm
      items={options}
      value={value}
      onChange={handleChange}
      onSelect={handleSelect}
      wrapper={DropItemComponent as ComponentType<{ item: DropdownItem }>}
      filter="contains"
    />
  );
};
